import { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  MultiSelect,
  Group,
  Paper,
  Button,
  TextInput,
  SegmentedControl,
  Select,
} from '@mantine/core';
import { useCompaniesByTaxId, useCompaniesBySearch } from '@api/companies';
import { useDebouncedValue } from '@mantine/hooks';
import { useSearchParams } from 'react-router-dom';

interface CompProps {
  opened: boolean;
  close: () => void;
}
import { useFilters, defaultState, Filters } from '@providers/filters';
import { DOCUMENTTYPEOPTIONSONFILTER } from '@constants';
import { DatePickerInput } from '@mantine/dates';
import { useCostCenters } from '@api/costCenters';
import { CostCenter } from '@types';
import { useAuth } from '@providers/auth';

function initCascade(id: string | null, items: CostCenter[]) {
  if (!id) return { l1: null, l2: null, l3: null };
  const current = items.find((c) => String(c.id) === id);
  if (!current) return { l1: null, l2: null, l3: null };
  if (current.level === 1) return { l1: id, l2: null, l3: null };
  if (current.level === 2) return { l1: String(current.parent_id), l2: id, l3: null };
  const l2 = items.find((c) => c.id === current.parent_id);
  return { l1: l2 ? String(l2.parent_id) : null, l2: String(current.parent_id), l3: id };
}

function FilterModal({ opened, close }: CompProps) {
  const { session } = useAuth();
  const costCentersAvailable = !!session.activeAccount?.cost_centers_available;
  const { filters: storeFilters, setFilters } = useFilters();
  const [filters, setFilters_] = useState<Filters>(storeFilters);
  const [_searchParams, setSearchParams] = useSearchParams();

  const [taxIdSearch, setTaxIdSearch] = useState('');
  const [debouncedTaxIdSearch] = useDebouncedValue(taxIdSearch, 400);
  const { data: taxIdCompanies, isLoading: isLoadingTaxId } = useCompaniesByTaxId(
    debouncedTaxIdSearch,
    { enabled: debouncedTaxIdSearch.length >= 2 },
  );

  const [legalNameSearch, setLegalNameSearch] = useState('');
  const [debouncedLegalNameSearch] = useDebouncedValue(legalNameSearch, 400);
  const { data: legalNameCompanies, isLoading: isLoadingLegalName } = useCompaniesBySearch(
    debouncedLegalNameSearch,
    { enabled: debouncedLegalNameSearch.length >= 2 },
  );
  const [selectedCompanies, setSelectedCompanies] = useState<{ value: string; label: string }[]>(
    [],
  );

  const { data: costCentersData } = useCostCenters();
  const activeCostCenters = (costCentersData?.data ?? []).filter((c) => !c.discarded_at);
  const hasCostCenters = activeCostCenters.some((c) => c.level === 1);

  const [ccL1, setCcL1] = useState<string | null>(null);
  const [ccL2, setCcL2] = useState<string | null>(null);
  const [ccL3, setCcL3] = useState<string | null>(null);

  useEffect(() => {
    if (opened && costCentersData) {
      const init = initCascade(storeFilters.cost_center_id ?? null, activeCostCenters);
      setCcL1(init.l1);
      setCcL2(init.l2);
      setCcL3(init.l3);
      setFilters_(storeFilters);
    }
  }, [opened, costCentersData]);

  const taxIdData = useMemo(() => {
    const fromSearch = taxIdCompanies?.data?.map((c) => c.tax_id as string) ?? [];
    return Array.from(new Set([...filters.tax_id, ...fromSearch]));
  }, [taxIdCompanies, filters.tax_id]);

  const legalNameData = useMemo(() => {
    const fromSearch =
      legalNameCompanies?.data?.map((c) => ({
        label: c.legal_name as string,
        value: String(c.id),
      })) ?? [];
    const all = [...selectedCompanies, ...fromSearch];
    return all.filter((item, index) => all.findIndex((i) => i.value === item.value) === index);
  }, [legalNameCompanies, selectedCompanies]);

  const l1Options = activeCostCenters
    .filter((c) => c.level === 1)
    .map((c) => ({ value: String(c.id), label: c.name }));

  const l2Options = activeCostCenters
    .filter((c) => c.level === 2 && (!ccL1 || c.parent_id === Number(ccL1)))
    .map((c) => {
      if (ccL1) return { value: String(c.id), label: c.name };
      const parent = activeCostCenters.find((p) => p.id === c.parent_id);
      return { value: String(c.id), label: parent ? `${parent.name} / ${c.name}` : c.name };
    });

  const l3Options = activeCostCenters
    .filter((c) => c.level === 3 && (!ccL2 || c.parent_id === Number(ccL2)))
    .map((c) => {
      if (ccL2) return { value: String(c.id), label: c.name };
      const parent = activeCostCenters.find((p) => p.id === c.parent_id);
      return { value: String(c.id), label: parent ? `${parent.name} / ${c.name}` : c.name };
    });

  const clearFilters = () => {
    setFilters(defaultState);
    setFilters_(defaultState);
    setSearchParams({});
    setSelectedCompanies([]);
    setCcL1(null);
    setCcL2(null);
    setCcL3(null);
  };

  const applyFilters = () => {
    const effectiveId = ccL3 ?? ccL2 ?? ccL1;
    setFilters({ ...filters, cost_center_id: effectiveId });
    close();
    setSearchParams({});
  };

  return (
    <Modal.Root size="lg" opened={opened} onClose={close} centered>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            <Text fz={20} fw={600}>
              Filtros
            </Text>
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <Stack>
            <Group justify="center" gap={60}>
              <DatePickerInput
                label="Fecha de emisión inicial"
                placeholder="Selecciona fecha"
                value={filters.start_date}
                valueFormat="YYYY-MM-DD"
                error={
                  filters.start_date && filters.end_date && filters.start_date > filters.end_date
                    ? 'Fecha inicial debe ser menor a la final'
                    : ''
                }
                onChange={(value) => {
                  setFilters_({ ...filters, start_date: value });
                }}
              />
              <DatePickerInput
                label="Fecha de emisión final"
                placeholder="Selecciona fecha"
                value={filters.end_date}
                valueFormat="YYYY-MM-DD"
                error={
                  filters.start_date && filters.end_date && filters.start_date > filters.end_date
                    ? 'Fecha final debe ser mayor a la inicial'
                    : ''
                }
                onChange={(value) => {
                  setFilters_({ ...filters, end_date: value });
                }}
              />
            </Group>
            <MultiSelect
              data={taxIdData}
              searchable
              searchValue={taxIdSearch}
              onSearchChange={setTaxIdSearch}
              filter={({ options }) => options}
              multiple
              label="RUT"
              nothingFoundMessage={
                taxIdSearch.length < 2
                  ? 'Ingresa al menos 2 caracteres para buscar'
                  : isLoadingTaxId || taxIdSearch !== debouncedTaxIdSearch
                    ? 'Buscando...'
                    : 'RUT no encontrado'
              }
              onChange={(value) => {
                setFilters_({ ...filters, tax_id: value });
              }}
              value={filters.tax_id}
            />
            <MultiSelect
              data={legalNameData}
              searchable
              searchValue={legalNameSearch}
              onSearchChange={setLegalNameSearch}
              filter={({ options }) => options}
              multiple
              label="Razón Social"
              nothingFoundMessage={
                legalNameSearch.length < 2
                  ? 'Ingresa al menos 2 caracteres para buscar'
                  : isLoadingLegalName || legalNameSearch !== debouncedLegalNameSearch
                    ? 'Buscando...'
                    : 'Razón Social no encontrada'
              }
              onChange={(value) => {
                setSelectedCompanies(legalNameData.filter((item) => value.includes(item.value)));
                setFilters_({ ...filters, company_id: value });
              }}
              value={filters.company_id}
            />
            <MultiSelect
              data={DOCUMENTTYPEOPTIONSONFILTER}
              searchable
              multiple
              label="Tipo documento"
              nothingFoundMessage="Tipo de documento no encontrado..."
              onChange={(value) => {
                setFilters_({ ...filters, document_type: value });
              }}
              value={filters.document_type}
            />
            <MultiSelect
              data={[
                { value: 'draft', label: 'Borrador' },
                { value: 'nil', label: 'Enviandose al SII' },
                { value: 'sii_waiting', label: 'Esperando respuesta SII' },
                { value: 'sii_rejected', label: 'Rechazado por SII' },
                { value: 'sii_accepted', label: 'Aceptado por SII' },
                {
                  value: 'sii_accepted_with_warnings',
                  label: 'Aceptado con observaciones por SII',
                },
                { value: 'company_not_received', label: 'No recibido por receptor' },
                { value: 'company_received', label: 'Recibido por receptor' },
                { value: 'company_accepted', label: 'Aceptado por receptor' },
                { value: 'company_rejected', label: 'Rechazado por receptor' },
                { value: 'canceled', label: 'Anulado' },
                { value: 'auto_accepted', label: 'Autoaceptado' },
                { value: 'sii_ceded', label: 'Documento cedido' },
              ]}
              searchable
              multiple
              label="Estado"
              nothingFoundMessage="Estado no encontrado..."
              onChange={(value) => {
                setFilters_({ ...filters, status: value });
              }}
              value={filters.status}
            />
            <TextInput
              label="Folio"
              placeholder="Folio"
              onChange={(event) => {
                setFilters_({ ...filters, number: event.currentTarget.value });
              }}
              value={filters.number}
            />
            <TextInput
              label="Código de Producto"
              placeholder="Código"
              onChange={(event) => {
                setFilters_({ ...filters, code: event.currentTarget.value });
              }}
              value={filters.code}
            />
            <TextInput
              label="Identificador externo"
              placeholder="Identificador externo"
              onChange={(event) => {
                setFilters_({ ...filters, external_id: event.currentTarget.value });
              }}
              value={filters.external_id}
            />
            {costCentersAvailable && hasCostCenters && (
              <Stack gap="xs">
                <Text fz="sm" fw={500}>
                  Plan de cuenta
                </Text>
                <Select
                  label="Nivel 1"
                  placeholder="Todos"
                  clearable
                  data={l1Options}
                  value={ccL1}
                  onChange={(v) => {
                    setCcL1(v);
                    setCcL2(null);
                    setCcL3(null);
                  }}
                />
                {l2Options.length > 0 && (
                  <Select
                    label={
                      ccL1
                        ? `Nivel 2 — ${activeCostCenters.find((c) => String(c.id) === ccL1)?.name}`
                        : 'Nivel 2'
                    }
                    placeholder="Todos"
                    clearable
                    data={l2Options}
                    value={ccL2}
                    onChange={(v) => {
                      setCcL2(v);
                      setCcL3(null);
                    }}
                  />
                )}
                {ccL2 && l3Options.length > 0 && (
                  <Select
                    label={`Nivel 3 — ${activeCostCenters.find((c) => String(c.id) === ccL2)?.name}`}
                    placeholder="Todos"
                    clearable
                    data={l3Options}
                    value={ccL3}
                    onChange={setCcL3}
                  />
                )}
              </Stack>
            )}
            <Stack gap={4}>
              <Text fz="sm" fw={500}>
                Estado de pago
              </Text>
              <SegmentedControl
                fullWidth
                data={[
                  { value: '', label: 'Todos' },
                  { value: 'true', label: 'Pagado' },
                  { value: 'false', label: 'No pagado' },
                ]}
                value={filters.paid}
                onChange={(value) => setFilters_({ ...filters, paid: value })}
              />
            </Stack>
            <Group justify="center">
              <Paper shadow="sm">
                <Button className="btn-outline-primary" onClick={clearFilters}>
                  Limpiar filtros
                </Button>
              </Paper>
              <Paper shadow="sm">
                <Button onClick={applyFilters}>Aplicar filtros</Button>
              </Paper>
            </Group>
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}

export default FilterModal;
